import { computed } from '@ember/object';
import { attr, belongsTo, hasMany } from '@ember-data/model';
import { iCalDateToJsDate, jsDateToiCalDate } from '@square/dashboard-items-data/utils/dates';
import NumericEnum from '@square/dashboard-utilities/utils/numeric-enum';
import Enum from '@square/dashboard-utilities/utils/enum';
import ItemsV3Base from '@square/dashboard-items-data/models/items-v3-base';
const PriceApplicationType = NumericEnum.create(['EACH', 'ALL']);
const PricingApplicationMode = NumericEnum.create(['UNKNOWN', 'AUTOMATIC', 'ATTACHED']);
const ExcludeStrategy = NumericEnum.create(['UNKNOWN', 'LEAST_EXPENSIVE', 'MOST_EXPENSIVE']);
const DiscountTargetScope = NumericEnum.create(['UNKNOWN', 'LINE_ITEM', 'WHOLE_PURCHASE']);
const SurchargeTargetScope = NumericEnum.create(['UNKNOWN', 'SURCHARGE_TARGET_SCOPE_LINE_ITEM', 'SURCHARGE_TARGET_SCOPE_WHOLE_PURCHASE']);
const AggregationStrategy = NumericEnum.create(['UNKNOWN', 'DO_NOT_USE', 'STACKABLE', 'EXCLUSIVE']);
const CatalogServiceChargeEcomFulfillmentType = new Enum(['UNKNOWN', 'PICKUP', 'DELIVERY', 'DINE_IN', 'SHIPPING']);
const PaymentType = NumericEnum.create(['UNKNOWN', 'CARD']);
export default ItemsV3Base.extend({
  name: attr('string'),
  discountTargetScope: attr('enum', {
    enumClass: DiscountTargetScope
  }),
  surchargeTargetScope: attr('enum', {
    enumClass: SurchargeTargetScope
  }),
  excludeStrategy: attr('enum', {
    enumClass: ExcludeStrategy
  }),
  stackable: attr('enum', {
    enumClass: AggregationStrategy
  }),
  dateTimeStart: attr('string'),
  dateTimeEnd: attr('string'),
  customerGroupIdsAny: attr('array'),
  presentAtAllLocations: attr('boolean'),
  targetProductSet: belongsTo('product-set', {
    autosaved: ['productSetsAll', 'productSetsAny'],
    async: false
  }),
  adjustedProductSet: belongsTo('product-set', {
    async: false
  }),
  excludeProductSet: belongsTo('product-set', {
    async: false
  }),
  timePeriods: hasMany('time-period'),
  dateRangePeriod: hasMany('time-period'),
  discount: belongsTo('discount'),
  surcharge: belongsTo('surcharge'),
  price: attr('legacy-money'),
  priceApplication: attr('enum', {
    enumClass: PriceApplicationType
  }),
  paymentType: attr('enum', {
    enumClass: PaymentType
  }),
  maxApplicationsPerAttachment: attr('number'),
  minimumOrderSubtotalMoney: attr('money-proto'),
  isDirty: computed('adjustedProductSet.isDirty', 'customerGroupIdsAny.isDirty', 'dirtyType', 'excludeProductSet.isDirty', 'targetProductSet.isDirty', 'timePeriods.@each.isDirty', 'hasDirtyFields', function () {
    return this._super() || this.timePeriods.any(timePeriod => timePeriod.isDirty) || this.customerGroupIdsAny?.isDirty || this.targetProductSet?.isDirty || this.adjustedProductSet?.isDirty || this.excludeProductSet?.isDirty;
  }),
  startDate: computed('dateTimeStart', {
    get() {
      if (this.dateTimeStart) {
        return iCalDateToJsDate(this.dateTimeStart);
      } else {
        return null;
      }
    },
    set(key, val) {
      this.set('dateTimeStart', val ? jsDateToiCalDate(val) : null);
      return val;
    }
  }),
  endDate: computed('dateTimeEnd', {
    get() {
      if (this.dateTimeEnd) {
        return iCalDateToJsDate(this.dateTimeEnd);
      } else {
        return null;
      }
    },
    set(key, val) {
      this.set('dateTimeEnd', val ? jsDateToiCalDate(val) : null);
      return val;
    }
  }),
  rollbackAttributes() {
    this.targetProductSet?.rollbackAttributes();
    this.adjustedProductSet?.rollbackAttributes();
    this.excludeProductSet?.rollbackAttributes();
    this.timePeriods?.forEach(timePeriod => {
      timePeriod?.rollbackAttributes();
    });
    this._super();
  },
  scheduleHours: computed('timePeriods.@each.toHours', function () {
    return this.timePeriods.mapBy('toHours').compact();
  }),
  almanacEventIds: computed('timePeriods.@each.toAlmanacEventId', function () {
    return this.timePeriods.mapBy('toAlmanacEventId').compact() || [];
  }),
  customHolidays: computed('timePeriods.[]', function () {
    return this.timePeriods.filter(timePeriod => timePeriod.name);
  }),
  pricingApplicationMode: attr('enum', {
    enumClass: PricingApplicationMode,
    defaultValue: PricingApplicationMode.AUTOMATIC
  }),
  enabledPricingSources: attr('array'),
  disabledPricingSources: attr('array'),
  ecomApplyToFulfillmentTypeServiceCharges: attr('array'),
  inheritEnabledUnits(from) {
    if (this.deleted) {
      return;
    }
    this.set('globallyEnabled', from.globallyEnabled);
    this.watchRelationship('enabledUnits', () => {
      this.set('enabledUnits', from.enabledUnits);
    });
    this.watchRelationship('disabledUnits', () => {
      this.set('disabledUnits', from.disabledUnits);
    });
    // Sets boolean based on if any disabled locations are in disabledUnits
    this.set('presentAtAllLocations', from.disabledUnits.toArray().length === 0);
  }
}).reopenClass({
  DiscountTargetScope,
  SurchargeTargetScope,
  ExcludeStrategy,
  PriceApplicationType,
  PricingApplicationMode,
  PaymentType,
  AggregationStrategy,
  CatalogServiceChargeEcomFulfillmentType,
  catalogType() {
    return 'PRICING_RULE';
  },
  _modelNameForMigration: 'pricing-rule'
});